/**
 * SheetView - Full brownfield grid component with useSheetData integration
 *
 * Story 4.6: Merge useSheetData into SheetView
 * - Integrates real data via useSheetData hook
 * - Preserves brownfield UX: toolbar, tabs, sidebar, formula bar
 * - Wire CRUD operations to API mutations
 *
 * Architecture Alignment:
 * - D3: View Architecture - Uses useSheetData hook (headless data layer)
 * - D4: State Management - React Query mutations from useSheetData
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DataGrid } from "./SmartGrid";
import { FormulaBar } from "./FormulaBar";
import { RightSidebar } from "./RightSidebar";
import { FilterModal } from "./FilterModal";
import { ColumnConfigModal } from "./ColumnConfigModal";
import { RowConfigModal } from "./RowConfigModal";
import type {
  GridRow,
  GridColumn,
  SelectionContext,
  CellFormula,
  Attachment,
  Comment,
  CellStyle,
  FilterGroup,
  ColumnType,
  DateRangeFilter,
} from "../types/gridTypes";
import { HistoryStack } from "../utils/historyStack";
// Updated Import to new Service
import {
  evaluateFormula,
  normalizeFormula,
  validateCell,
} from "../services/formulaEngine";
import { applyFilters, applySearch } from "../utils/filterUtils";
import { computeRowAggregations } from "../utils/rowAggregation";
import { uploadAttachment, deleteAttachment } from "../../../api/sheets.js";
import "../styles/theme.css";
import "../styles/sheet.css";
import "../styles/tabBar.css";
import type { Tab } from "./TabBar";
import { TabBar } from "./TabBar";
import { GridStatusBar } from "./GridStatusBar";
import {
  IconUndo,
  IconRedo,
  IconBold,
  IconItalic,
  IconUnderline,
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
  IconIndent,
  IconOutdent,
  IconPlus,
  IconSearch,
  IconFilter,
  IconX,
  IconSparkles,
} from "./icons";

// useSheetData integration (Story 4.6)
import { useSheetData, type NewField } from "../../../hooks/useSheetData";
import type { Field, FieldType } from "../../../types/types/field";
import type { Row } from "../../../types/types/row";
import type { CellValue } from "../../../types/types/cell";

// ModuleToolbarContext integration (Story 6.1 - AC-6.1.4)
import { useModuleToolbar } from "../../../components/module/ModuleToolbarContext";

// =============================================================================
// TYPES (Story 4.6 - Task 1.1)
// =============================================================================

/**
 * SheetView Props
 * Story 4.6: AC-4.6.1 - SheetView requires sheetId to fetch real data
 */
export interface SheetViewProps {
  /** Sheet ID to load and manage */
  sheetId: string;
  /** Read-only mode */
  readOnly?: boolean;
  /**
   * Hide the bottom TabBar - useful when embedding SheetView in a module with its own tab navigation
   */
  hideTabBar?: boolean;
  /**
   * Hide the internal toolbar - useful when SheetView is wrapped with ModuleToolbar
   * Story 6.1: AC-6.1.1 - Toolbar persistence via unified ModuleToolbar
   */
  hideToolbar?: boolean;
  /**
   * Callback when confirmation is required for field deletion
   * Story 3.5: AC-3.10 - Shows confirmation dialog with row count
   */
  onConfirmFieldDelete?: (
    fieldId: string,
    fieldName: string,
    rowCount: number,
  ) => Promise<boolean>;
}

// =============================================================================
// TYPE TRANSFORMATIONS (Story 4.6 - Task 8)
// Copied from WorksheetView.tsx lines 57-116
// =============================================================================

/**
 * Map Field type to GridColumn type
 * Field uses 'multi_select', Grid uses 'multi-select'
 */
function mapFieldTypeToColumnType(fieldType: FieldType): ColumnType {
  // Handle the underscore vs hyphen difference
  if (fieldType === "multi_select") return "multi-select";
  // These types exist in both - just cast
  const directMappedTypes: ColumnType[] = [
    "text",
    "number",
    "currency",
    "percent",
    "date",
    "checkbox",
    "select",
    "rating",
    "formula",
  ];
  if (directMappedTypes.includes(fieldType as ColumnType)) {
    return fieldType as ColumnType;
  }
  // Default fallback
  return "text";
}

function normalizeRowFormulas(
  formulas?: Record<string, unknown> | null,
): Record<string, CellFormula> | undefined {
  if (!formulas) return undefined;

  const result: Record<string, CellFormula> = {};
  Object.entries(formulas).forEach(([fieldId, value]) => {
    if (!value) return;
    if (typeof value === "string") {
      result[fieldId] = { raw: value, normalized: value };
    } else if (
      typeof value === "object" &&
      "raw" in (value as Record<string, unknown>) &&
      "normalized" in (value as Record<string, unknown>)
    ) {
      const val = value as CellFormula;
      result[fieldId] = { raw: val.raw, normalized: val.normalized };
    }
  });

  return Object.keys(result).length ? result : undefined;
}

/**
 * Transform Field to GridColumn format
 * AC-3.34: Grid receives data in its expected format
 * AC-5.4.7: Passes semanticType for duration formatting
 */
function fieldToGridColumn(field: Field): GridColumn {
  return {
    id: field.id,
    label: field.name,
    type: mapFieldTypeToColumnType(field.type),
    width: field.width || 150,
    options: field.config?.options?.map((opt) =>
      typeof opt === "string" ? opt : opt.value,
    ),
    readOnly: field.locked,
    formulaExpression:
      field.type === "formula" && typeof field.config?.formula === "string"
        ? field.config.formula
        : undefined,
    // AC-5.4.7: Pass semanticType for duration formatting in grid
    semanticType: field.config?.semanticType as GridColumn["semanticType"],
    // First column is typically primary
    isPrimary: field.position === 0,
  };
}

/**
 * Transform Row to GridRow format
 */
function rowToGridRow(row: Row): GridRow {
  return {
    id: row.id,
    values: row.data as Record<string, unknown>,
    formulas: normalizeRowFormulas(
      row.formulas as Record<string, unknown> | null,
    ),
    expanded: true,
    rowType: "data",
    attachments: row.attachments || [],
    attachmentCount: row.attachments?.length || 0,
  };
}

/**
 * Extract changes from GridRow compared to original Row
 * Returns the changed field IDs and values
 */
function getRowChanges(
  originalRow: Row | undefined,
  gridRow: GridRow,
): { fieldId: string; value: CellValue }[] {
  if (!originalRow) return [];

  const changes: { fieldId: string; value: CellValue }[] = [];
  const originalData = originalRow.data;
  const newData = gridRow.values;

  // Check each field in new data
  for (const fieldId of Object.keys(newData)) {
    if (originalData[fieldId] !== newData[fieldId]) {
      changes.push({ fieldId, value: newData[fieldId] as CellValue });
    }
  }

  return changes;
}

const IconColumns = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125Z"
    />
  </svg>
);

const IconTable = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m13.5 2.625v-1.5c0-.621.504-1.125 1.125-1.125m-13.5 0v-3.75C3.375 7.5 4.25 6.75 5.625 6.75h13.5c.621 0 1.125.504 1.125 1.125v3.75m-13.5 0v1.5c0 .621.504 1.125 1.125 1.125m13.5-2.625v1.5c0 .621.504 1.125 1.125 1.125M10.5 19.5v-1.5m0 1.5v-1.5m0 1.5h3.75m-3.75 0H10.5m-1.125 0H9.375m-1.125 0H3.375m17.25 0H15M21 18.375v-3.75m-17.25 0H21"
    />
  </svg>
);

/**
 * SheetView Component
 *
 * Story 4.6: Merge useSheetData into SheetView
 * Full brownfield grid with:
 * - useSheetData integration for real CRUD operations
 * - Full toolbar, tabs, sidebar, formula bar
 * - Local history stack for undo/redo
 */
export const SheetView: React.FC<SheetViewProps> = ({
  sheetId,
  readOnly = false,
  hideTabBar = false,
  hideToolbar = false,
  onConfirmFieldDelete,
}) => {
  // ---------------------------------------------------------------------------
  // DATA LAYER (Story 4.6 - AC-4.6.1, AC-4.6.2)
  // ---------------------------------------------------------------------------

  const {
    fields,
    rows: apiRows,
    isLoading,
    error,
    refetch,
    addRow: apiAddRow,
    updateCell,
    deleteRow: apiDeleteRow,
    addField,
    updateField,
    deleteField,
    reorderFields,
  } = useSheetData(sheetId);

  // ModuleToolbarContext integration (Story 6.1 - AC-6.1.4)
  // Register undo/redo handlers with parent toolbar when hideToolbar=true
  const { registerUndoRedo, unregisterUndoRedo } = useModuleToolbar();

  // ---------------------------------------------------------------------------
  // DATA TRANSFORMATION (AC-4.6.1 - Task 1.3)
  // ---------------------------------------------------------------------------

  // Transform Field[] to GridColumn[]
  const columns = useMemo(() => fields.map(fieldToGridColumn), [fields]);

  // Transform Row[] to GridRow[]
  const gridRows = useMemo(() => apiRows.map(rowToGridRow), [apiRows]);

  // Create a lookup map for original rows (for change detection)
  const rowsMap = useMemo(
    () => new Map(apiRows.map((r) => [r.id, r])),
    [apiRows],
  );

  // ---------------------------------------------------------------------------
  // LOCAL STATE (History, UI state from brownfield)
  // ---------------------------------------------------------------------------

  // Stable keys for detecting actual data changes (prevents infinite loop from reference changes)
  const gridRowsKey = JSON.stringify(gridRows);
  const columnsKey = JSON.stringify(columns.map((c) => c.id));

  // History Stack - initialized with useSheetData rows once loaded (Story 4.6 - Task 12)
  const historyRef = useRef<HistoryStack<GridRow[]> | null>(null);
  const [historyState, setHistoryState] = useState({
    canUndo: false,
    canRedo: false,
  });

  // Initialize/update history when data loads
  // Only depend on gridRowsKey to prevent infinite loops (gridRows reference changes on every render)
  useEffect(() => {
    if (!isLoading && gridRows.length > 0 && !historyRef.current) {
      historyRef.current = new HistoryStack<GridRow[]>(gridRows);
      setHistoryState({ canUndo: false, canRedo: false });
    }
  }, [isLoading, gridRowsKey]);

  // ---------------------------------------------------------------------------
  // MODULE TOOLBAR INTEGRATION (Story 6.1 - AC-6.1.4)
  // ---------------------------------------------------------------------------

  // Register undo/redo handlers with ModuleToolbarContext when hideToolbar=true
  // This allows the parent ModuleToolbar to control undo/redo for SheetView
  useEffect(() => {
    if (!hideToolbar) return; // Only register when using parent toolbar

    const handleUndo = () => {
      if (!historyRef.current) return;
      const prev = historyRef.current.undo();
      if (prev) {
        setLocalRows([...prev]);
        setHistoryState({
          canUndo: historyRef.current.canUndo(),
          canRedo: historyRef.current.canRedo(),
        });
      }
    };

    const handleRedo = () => {
      if (!historyRef.current) return;
      const next = historyRef.current.redo();
      if (next) {
        setLocalRows([...next]);
        setHistoryState({
          canUndo: historyRef.current.canUndo(),
          canRedo: historyRef.current.canRedo(),
        });
      }
    };

    registerUndoRedo({
      onUndo: handleUndo,
      onRedo: handleRedo,
      canUndo: historyState.canUndo,
      canRedo: historyState.canRedo,
    });

    return () => {
      unregisterUndoRedo();
    };
  }, [
    hideToolbar,
    historyState.canUndo,
    historyState.canRedo,
    registerUndoRedo,
    unregisterUndoRedo,
  ]);

  // Listen for 'timekeeping:refresh' event to refresh data when ModuleToolbar triggers refresh
  useEffect(() => {
    const handleRefresh = () => {
      refetch();
    };

    window.addEventListener("timekeeping:refresh", handleRefresh);
    return () => {
      window.removeEventListener("timekeeping:refresh", handleRefresh);
    };
  }, [refetch]);

  // Local rows state for formula evaluation and styling (kept in sync with API data)
  const [localRows, setLocalRows] = useState<GridRow[]>([]);

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Sync local rows with API rows when they change
  // Only depend on gridRowsKey to prevent infinite loops (gridRows reference changes on every render)
  useEffect(() => {
    setLocalRows(gridRows);
  }, [gridRowsKey]);

  // Local columns state for visibility toggles and local edits
  const [localColumns, setLocalColumns] = useState<GridColumn[]>([]);

  // Sync local columns with API fields
  // Only depend on columnsKey to prevent infinite loops (columns reference changes on every render)
  useEffect(() => {
    setLocalColumns(columns);
  }, [columnsKey]);

  // Tab State (Demo - AC-4.6.4: Keep as local for MVP)
  // TODO: Wire to Views API in future story
  const [tabs, setTabs] = useState<Tab[]>([
    { id: "default", name: "Default View", isActive: true },
  ]);

  // Selection
  const [selection, setSelection] = useState<SelectionContext>({
    gridId: sheetId,
    selectedRowIds: [],
    selectedColumnIds: [],
    mode: "none",
  });

  // Sidebar State
  const [sidebarTab, setSidebarTab] = useState<
    "attachments" | "comments" | "summary" | "ai" | "none"
  >("none");

  // Formula Bar Toggle (Story 6.5: AC-6.5.1)
  // Formula bar shows when user types `=` in a cell
  const [showFormulaBar, setShowFormulaBar] = useState(false);
  // Track formula editing state (used to hide formula bar on commit/cancel)
  const [, setFormulaEditingCell] = useState<{
    rowId: string;
    colId: string;
  } | null>(null);
  // Story 6.5: AC-6.5.2 - Sync edit value between cell and formula bar
  const [cellEditValue, setCellEditValue] = useState<string>("");

  // Sorting State
  const [sortConfig, setSortConfig] = useState<{
    colId: string;
    direction: "asc" | "desc";
  } | null>(null);

  // Filter & Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeFilterGroup, setActiveFilterGroup] = useState<
    FilterGroup | undefined
  >(undefined);
  const [dateRangeFilter, setDateRangeFilter] =
    useState<DateRangeFilter | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Save status indicator (AC-4.6.3 - Task 3.2)
  const [saveStatus, setSaveStatus] = useState<"saved" | "saving" | "error">(
    "saved",
  );

  // Column & Row Config State
  const [editingColumn, setEditingColumn] = useState<GridColumn | null>(null);
  const [pendingInsertIndex, setPendingInsertIndex] = useState<number | null>(
    null,
  );
  const [configuringRowId, setConfiguringRowId] = useState<string | null>(null);

  // Menus
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);

  // Formula Range Capture State
  const [rangeCaptureCallback, setRangeCaptureCallback] = useState<
    ((expr: string) => void) | null
  >(null);

  // Click-outside handler removed - menu closes via item onClick or blur
  // The dropdown items already call setShowAddMenu(false) after their action

  // ---------------------------------------------------------------------------
  // DERIVED DATA (Filtering + Search)
  // ---------------------------------------------------------------------------

  const filteredRows = useMemo(() => {
    let result = localRows;

    if (activeFilterGroup) {
      result = applyFilters(result, localColumns, activeFilterGroup);
    }
    if (searchQuery) {
      result = applySearch(result, localColumns, searchQuery);
    }
    return result;
  }, [localRows, localColumns, activeFilterGroup, searchQuery]);

  // Validation Logic
  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    let count = 0;
    localRows.forEach((row) => {
      localColumns.forEach((col) => {
        if (col.hidden) return;
        const result = validateCell(row.values[col.id], col);
        if (result && !result.isValid && result.message) {
          errors[`${row.id}:${col.id}`] = result.message;
          count++;
        }
      });
    });
    return { errors, count };
  }, [localRows, localColumns]);

  // Active Cell Helpers
  const activeRow = localRows.find((r) => r.id === selection.primaryRowId);
  const activeCol = localColumns.find(
    (c) => c.id === selection.primaryColumnId,
  );

  let activeFormula: CellFormula | undefined = undefined;
  if (activeRow && activeCol) {
    if (activeRow.formulas?.[activeCol.id]) {
      activeFormula = activeRow.formulas[activeCol.id];
    } else if (activeCol.type === "formula" && activeCol.formulaExpression) {
      activeFormula = {
        raw: activeCol.formulaExpression,
        normalized: normalizeFormula(activeCol.formulaExpression, localColumns),
      };
    }
  }

  const activeValue =
    activeRow && activeCol ? activeRow.values[activeCol.id] : undefined;

  const activeRowIndex = activeRow
    ? localRows.findIndex((r) => r.id === activeRow.id)
    : -1;
  const activeAddress =
    activeRowIndex !== -1 && activeCol
      ? `Row ${activeRowIndex + 1}, ${activeCol.label}`
      : null;

  // --- Core Logic ---

  /**
   * Update local rows with formula evaluation and aggregation
   * Story 4.6 - Task 11: Preserve Formula Evaluation
   */
  const updateRowsInternal = useCallback(
    (newRows: GridRow[], overrideColumns?: GridColumn[]) => {
      const effectiveColumns = overrideColumns || localColumns;

      // 1. Formula Evaluation Phase (Story 4.6 - AC-4.6.1)
      let computedRows: GridRow[] = newRows.map((r) => {
        const rowClone = {
          ...r,
          values: { ...r.values },
          formulas: { ...r.formulas },
          cellStyles: r.cellStyles ? { ...r.cellStyles } : undefined,
        };

        effectiveColumns.forEach((col) => {
          const val = rowClone.values[col.id];
          if (typeof val === "string" && val.startsWith("=")) {
            const norm = normalizeFormula(val, effectiveColumns);
            if (!rowClone.formulas) rowClone.formulas = {};
            rowClone.formulas[col.id] = { raw: val, normalized: norm };
          }
          let effectiveFormula: CellFormula | undefined =
            rowClone.formulas?.[col.id];
          if (
            !effectiveFormula &&
            col.type === "formula" &&
            col.formulaExpression
          ) {
            effectiveFormula = {
              raw: col.formulaExpression,
              normalized: normalizeFormula(
                col.formulaExpression,
                effectiveColumns,
              ),
            };
          }
          if (effectiveFormula) {
            const { value } = evaluateFormula(effectiveFormula.raw, {
              row: r,
              allRows: newRows,
              columns: effectiveColumns,
              targetColumnId: col.id,
            });
            rowClone.values[col.id] = value;
          }
        });
        return rowClone;
      });

      // 2. Row Aggregation Phase
      computedRows = computeRowAggregations(computedRows, effectiveColumns);

      // 3. Update history stack (Story 4.6 - Task 12)
      if (historyRef.current) {
        historyRef.current.push(computedRows);
        setHistoryState({
          canUndo: historyRef.current.canUndo(),
          canRedo: historyRef.current.canRedo(),
        });
      }

      setLocalRows(computedRows);
      if (overrideColumns) setLocalColumns(overrideColumns);
    },
    [localColumns],
  );

  /**
   * Handle changes from DataGrid
   * Story 4.6 - Task 2: Wire CRUD Operations to useSheetData
   * AC-4.6.2: Changes persist to database via useSheetData mutations
   */
  const handleGridChange = useCallback(
    async (incomingRows: GridRow[]) => {
      if (readOnly) return;

      const incomingMap = new Map(incomingRows.map((r) => [r.id, r]));
      const incomingIds = new Set(incomingRows.map((r) => r.id));
      const previousIds = new Set(localRows.map((r) => r.id));

      // --- DETECT DELETED ROWS (AC-4.6.2 - Task 2.3) ---
      const deletedIds = [...previousIds].filter((id) => !incomingIds.has(id));
      for (const rowId of deletedIds) {
        try {
          setSaveStatus("saving");
          await apiDeleteRow(rowId);
          setSaveStatus("saved");
        } catch {
          setSaveStatus("error");
          // Error handled by useSheetData with toast
        }
      }

      // --- DETECT NEW ROWS (AC-4.6.2 - Task 2.1) ---
      for (const gridRow of incomingRows) {
        if (gridRow.id.startsWith("row_") && !rowsMap.has(gridRow.id)) {
          // New row from Grid - create via API
          try {
            setSaveStatus("saving");
            await apiAddRow(gridRow.values as Record<string, CellValue>);
            setSaveStatus("saved");
          } catch {
            setSaveStatus("error");
            // Error handled by useSheetData with toast
          }
        }
      }

      // --- DETECT CELL EDITS (AC-4.6.2 - Task 2.2) ---
      for (const gridRow of incomingRows) {
        // Skip new rows (already handled above)
        if (gridRow.id.startsWith("row_")) continue;

        const originalRow = rowsMap.get(gridRow.id);
        if (!originalRow) continue;

        const changes = getRowChanges(originalRow, gridRow);
        for (const change of changes) {
          try {
            setSaveStatus("saving");
            await updateCell(gridRow.id, change.fieldId, change.value);
            setSaveStatus("saved");
          } catch {
            setSaveStatus("error");
            // Error handled by useSheetData with toast
          }
        }
      }

      // Update local state for immediate UI feedback (formula eval, styling)
      if (localRows.length === filteredRows.length) {
        updateRowsInternal(incomingRows);
        return;
      }

      const nextRows: GridRow[] = [];
      localRows.forEach((existingRow) => {
        if (deletedIds.includes(existingRow.id)) return;
        if (incomingMap.has(existingRow.id))
          nextRows.push(incomingMap.get(existingRow.id)!);
        else nextRows.push(existingRow);
      });
      incomingRows.forEach((r) => {
        if (!localRows.find((ex) => ex.id === r.id)) nextRows.push(r);
      });
      updateRowsInternal(nextRows);
    },
    [
      readOnly,
      localRows,
      filteredRows,
      rowsMap,
      apiAddRow,
      apiDeleteRow,
      updateCell,
      updateRowsInternal,
    ],
  );

  /**
   * Handle column save (add or update)
   * Story 4.6 - Task 2.4: Wire column operations to field mutations
   */
  const handleColumnSave = useCallback(
    async (col: GridColumn) => {
      if (pendingInsertIndex !== null) {
        // Adding new column - call API
        const newField: NewField = {
          name: col.label,
          type:
            col.type === "multi-select"
              ? "multi_select"
              : (col.type as FieldType),
          width: col.width,
        };
        try {
          setSaveStatus("saving");
          await addField(newField);
          setSaveStatus("saved");
        } catch {
          setSaveStatus("error");
          // Error handled by useSheetData
        }
      } else {
        // Story 6.3: Updating existing column - call updateField API
        // AC-6.3.4: Column renames automatically update formulas via useSheetData
        const existingField = fields.find((f) => f.id === col.id);
        if (existingField) {
          try {
            setSaveStatus("saving");
            await updateField(col.id, {
              name: col.label,
              type:
                col.type === "multi-select"
                  ? "multi_select"
                  : (col.type as FieldType),
              width: col.width,
            });
            setSaveStatus("saved");
          } catch {
            setSaveStatus("error");
            // Error handled by useSheetData
          }
        }
        // Also update local state for immediate UI feedback
        const newColumns = localColumns.map((c) => (c.id === col.id ? col : c));
        updateRowsInternal(localRows, newColumns);
      }
      setEditingColumn(null);
      setPendingInsertIndex(null);
    },
    [
      pendingInsertIndex,
      localColumns,
      localRows,
      fields,
      addField,
      updateField,
      updateRowsInternal,
    ],
  );

  /**
   * Handle column deletion
   * Story 4.6 - Task 2.4: Wire column operations to field mutations
   * AC-4.6.2: deleteField called with confirmation if data exists
   */
  const handleDeleteColumn = useCallback(
    async (colId: string) => {
      const col = localColumns.find((c) => c.id === colId);
      if (col?.isPrimary) {
        alert("Cannot delete the Primary column.");
        return;
      }

      // Check if field has data and count rows with data
      const rowsWithData = apiRows.filter((row) => {
        const value = row.data[colId];
        return value !== null && value !== undefined && value !== "";
      });
      const fieldHasData = rowsWithData.length > 0;

      const field = fields.find((f) => f.id === colId);

      if (fieldHasData) {
        // Show confirmation dialog
        if (onConfirmFieldDelete) {
          const confirmed = await onConfirmFieldDelete(
            colId,
            field?.name || "Column",
            rowsWithData.length,
          );
          if (!confirmed) return;
        } else {
          // Fallback to browser confirm
          const confirmed = window.confirm(
            `The column "${field?.name || "Column"}" contains data in ${rowsWithData.length} rows. Are you sure you want to delete it?`,
          );
          if (!confirmed) return;
        }
        // Delete with confirmation
        try {
          setSaveStatus("saving");
          await deleteField(colId, true);
          setSaveStatus("saved");
        } catch {
          setSaveStatus("error");
        }
      } else {
        // No data, delete immediately
        try {
          setSaveStatus("saving");
          await deleteField(colId);
          setSaveStatus("saved");
        } catch {
          setSaveStatus("error");
        }
      }
    },
    [localColumns, apiRows, fields, onConfirmFieldDelete, deleteField],
  );

  const handleInitiateInsertColumn = (
    targetColId: string,
    position: "left" | "right",
  ) => {
    const idx = localColumns.findIndex((c) => c.id === targetColId);
    if (idx === -1) return;
    const insertIndex = position === "left" ? idx : idx + 1;
    const newCol: GridColumn = {
      id: `col_${Math.random().toString(36).substr(2, 9)}`,
      label: "New Column",
      type: "text",
      width: 150,
    };
    setEditingColumn(newCol);
    setPendingInsertIndex(insertIndex);
  };

  const handleInitiateAddColumn = () => {
    const newCol: GridColumn = {
      id: `col_${Math.random().toString(36).substr(2, 9)}`,
      label: "New Column",
      type: "text",
      width: 150,
    };
    setEditingColumn(newCol);
    setPendingInsertIndex(localColumns.length);
  };

  const handleEditColumn = (colId: string) => {
    const col = localColumns.find((c) => c.id === colId);
    if (col) {
      setEditingColumn(col);
      setPendingInsertIndex(null);
    }
  };

  const handleRowConfig = (rowId: string) => {
    setConfiguringRowId(rowId);
  };

  const handleSaveRowConfig = (updatedRow: GridRow) => {
    const newRows = localRows.map((r) =>
      r.id === updatedRow.id ? updatedRow : r,
    );
    updateRowsInternal(newRows);
    setConfiguringRowId(null);
  };

  /**
   * Handle column reorder with API persistence
   * Story 4.6 - Task 2.4: Wire column operations to field mutations
   */
  const handleColumnReorder = useCallback(
    async (fromColId: string, toColId: string) => {
      const fromIdx = localColumns.findIndex((c) => c.id === fromColId);
      const toIdx = localColumns.findIndex((c) => c.id === toColId);
      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

      // Optimistic local update
      const newCols = [...localColumns];
      const [movedCol] = newCols.splice(fromIdx, 1);
      newCols.splice(toIdx, 0, movedCol);
      setLocalColumns(newCols);

      // Persist to backend
      try {
        setSaveStatus("saving");
        await reorderFields(newCols.map((c) => c.id));
        setSaveStatus("saved");
      } catch {
        setSaveStatus("error");
        // Rollback on error
        setLocalColumns(localColumns);
      }
    },
    [localColumns, reorderFields],
  );

  const toggleColumnVisibility = (colId: string) => {
    const newCols = localColumns.map((c) =>
      c.id === colId ? { ...c, hidden: !c.hidden } : c,
    );
    setLocalColumns(newCols);
  };

  const getDescendantIds = (rowId: string, allRows: GridRow[]): string[] => {
    const children = allRows.filter((r) => r.parentId === rowId);
    let results: string[] = [];
    children.forEach((c) => {
      results.push(c.id);
      results = results.concat(getDescendantIds(c.id, allRows));
    });
    return results;
  };

  const handleRowReorder = (fromRowId: string, toRowId: string) => {
    const fromIndex = localRows.findIndex((r) => r.id === fromRowId);
    const toIndex =
      toRowId === "__END__"
        ? localRows.length
        : localRows.findIndex((r) => r.id === toRowId);
    if (fromIndex === -1 || toIndex === fromIndex) return;
    const descendantIds = getDescendantIds(fromRowId, localRows);
    const blockIds = new Set([fromRowId, ...descendantIds]);
    if (toRowId !== "__END__" && descendantIds.includes(toRowId)) return;
    const blockRows: GridRow[] = [];
    const remainingRows: GridRow[] = [];
    localRows.forEach((r) => {
      if (blockIds.has(r.id)) blockRows.push(r);
      else remainingRows.push(r);
    });
    let insertPos = remainingRows.length;
    if (toRowId !== "__END__") {
      const newToIndex = remainingRows.findIndex((r) => r.id === toRowId);
      const insertAfter = toIndex > fromIndex;
      insertPos = insertAfter ? newToIndex + 1 : newToIndex;
    }
    const reorderedRows = [
      ...remainingRows.slice(0, insertPos),
      ...blockRows,
      ...remainingRows.slice(insertPos),
    ];
    updateRowsInternal(reorderedRows);
  };

  /**
   * Undo handler - Story 4.6 Task 12
   */
  const handleUndo = () => {
    if (!historyRef.current) return;
    const prev = historyRef.current.undo();
    if (prev) {
      setLocalRows([...prev]);
      setHistoryState({
        canUndo: historyRef.current.canUndo(),
        canRedo: historyRef.current.canRedo(),
      });
    }
  };

  /**
   * Redo handler - Story 4.6 Task 12
   */
  const handleRedo = () => {
    if (!historyRef.current) return;
    const next = historyRef.current.redo();
    if (next) {
      setLocalRows([...next]);
      setHistoryState({
        canUndo: historyRef.current.canUndo(),
        canRedo: historyRef.current.canRedo(),
      });
    }
  };

  const handleApplyCellStyle = (stylePatch: CellStyle) => {
    const targetRows = localRows.map((r) => {
      const isRowSelected = selection.selectedRowIds.includes(r.id);
      if (!isRowSelected) return r;
      const newCellStyles = { ...(r.cellStyles || {}) };
      let changed = false;
      const targetColIds =
        selection.mode === "row"
          ? localColumns.map((c) => c.id)
          : selection.selectedColumnIds;
      targetColIds.forEach((colId) => {
        const currentStyle = newCellStyles[colId] || {};
        newCellStyles[colId] = { ...currentStyle, ...stylePatch };
        changed = true;
      });
      if (changed) return { ...r, cellStyles: newCellStyles };
      return r;
    });
    if (targetRows !== localRows) updateRowsInternal(targetRows);
  };

  const handleIndentRows = (direction: "indent" | "outdent") => {
    const selectedIds = selection.selectedRowIds;
    if (selectedIds.length === 0) return;
    const newRows = [...localRows];
    const rowMap = new Map(newRows.map((r) => [r.id, r]));
    if (direction === "indent") {
      for (let i = 0; i < newRows.length; i++) {
        const r = newRows[i];
        if (selectedIds.includes(r.id)) {
          const prevRow = i > 0 ? newRows[i - 1] : null;
          if (prevRow && prevRow.parentId !== r.id) {
            newRows[i] = { ...r, parentId: prevRow.id };
            const prevIdx = newRows.findIndex((row) => row.id === prevRow.id);
            if (prevIdx > -1)
              newRows[prevIdx] = { ...newRows[prevIdx], expanded: true };
          }
        }
      }
    } else {
      for (let i = 0; i < newRows.length; i++) {
        const r = newRows[i];
        if (selectedIds.includes(r.id)) {
          if (r.parentId) {
            const parent = rowMap.get(r.parentId);
            const grandParentId = parent ? parent.parentId : null;
            newRows[i] = { ...r, parentId: grandParentId || undefined };
          }
        }
      }
    }
    updateRowsInternal(newRows);
  };

  /**
   * Handle formula commit from FormulaBar
   * Story 4.6 - Task 11: Preserve Formula Evaluation
   * Story 6.5 - AC-6.5.5: Hide formula bar on commit
   */
  const handleFormulaCommit = useCallback(
    async (val: string) => {
      if (!activeRow || !activeCol) return;
      const isFormula = val.startsWith("=");
      const newRows = localRows.map((r) => {
        if (r.id !== activeRow.id) return r;
        const rClone = {
          ...r,
          values: { ...r.values },
          formulas: { ...r.formulas },
          cellStyles: r.cellStyles ? { ...r.cellStyles } : undefined,
        };
        if (isFormula) {
          const norm = normalizeFormula(val, localColumns);
          if (!rClone.formulas) rClone.formulas = {};
          rClone.formulas[activeCol.id] = { raw: val, normalized: norm };
        } else {
          if (rClone.formulas) delete rClone.formulas[activeCol.id];
          let v: unknown = val;
          if (
            activeCol.type === "number" ||
            activeCol.type === "currency" ||
            activeCol.type === "percent"
          ) {
            const cleanVal = val.replace(/[^0-9.-]+/g, "");
            const f = parseFloat(cleanVal);
            if (!isNaN(f) && cleanVal !== "") v = f;
            else if (val === "") v = null;
          }
          if (activeCol.type === "rating") v = val;
          rClone.values[activeCol.id] = v;
        }
        return rClone;
      });
      updateRowsInternal(newRows);

      // Story 6.5 - AC-6.5.5: Hide formula bar on commit
      setShowFormulaBar(false);
      setFormulaEditingCell(null);

      // Persist non-formula value to database (AC-4.6.2)
      if (!isFormula && activeRow && activeCol) {
        let persistValue: CellValue = val;
        if (
          activeCol.type === "number" ||
          activeCol.type === "currency" ||
          activeCol.type === "percent"
        ) {
          const cleanVal = val.replace(/[^0-9.-]+/g, "");
          const f = parseFloat(cleanVal);
          if (!isNaN(f) && cleanVal !== "") persistValue = f;
          else if (val === "") persistValue = null;
        }
        try {
          setSaveStatus("saving");
          await updateCell(activeRow.id, activeCol.id, persistValue);
          setSaveStatus("saved");
        } catch {
          setSaveStatus("error");
        }
      }
    },
    [
      activeRow,
      activeCol,
      localRows,
      localColumns,
      updateRowsInternal,
      updateCell,
    ],
  );

  const handleSort = (colId: string) => {
    const currentDirection =
      sortConfig?.colId === colId ? sortConfig.direction : null;
    const newDirection = currentDirection === "asc" ? "desc" : "asc";
    const childrenMap = new Map<string | undefined, GridRow[]>();
    localRows.forEach((r) => {
      const pid = r.parentId || "root";
      if (!childrenMap.has(pid)) childrenMap.set(pid, []);
      childrenMap.get(pid)!.push(r);
    });
    const processLevel = (pid: string | undefined): GridRow[] => {
      const siblings = childrenMap.get(pid) || [];
      siblings.sort((a, b) => {
        const valA = a.values[colId];
        const valB = b.values[colId];
        if (valA === valB) return 0;
        if (valA === null || valA === undefined || valA === "") return 1;
        if (valB === null || valB === undefined || valB === "") return -1;
        if (valA < valB) return newDirection === "asc" ? -1 : 1;
        return newDirection === "asc" ? 1 : -1;
      });
      let result: GridRow[] = [];
      siblings.forEach((sib) => {
        result.push(sib);
        const children = processLevel(sib.id);
        result = result.concat(children);
      });
      return result;
    };
    const sortedRows = processLevel("root");
    updateRowsInternal(sortedRows);
    setSortConfig({ colId, direction: newDirection });
  };

  /**
   * Handle save button click
   * Story 4.6 - Task 3: Wire Save Button to Persistence
   * AC-4.6.3: useSheetData uses optimistic updates, changes persist immediately
   */
  const handleSave = () => {
    if (validationErrors.count > 0) {
      alert(`Cannot save: ${validationErrors.count} validation errors found.`);
      return;
    }
    // Changes already persisted via optimistic updates (AC-4.6.3)
    // Show confirmation to user
    setSaveStatus("saved");
  };

  const handleValidationClick = () => {
    const keys = Object.keys(validationErrors.errors);
    if (keys.length > 0) {
      const [rId, cId] = keys[0].split(":");
      setSelection({
        gridId: sheetId,
        mode: "cell",
        primaryRowId: rId,
        primaryColumnId: cId,
        selectedRowIds: [rId],
        selectedColumnIds: [cId],
      });
    }
  };

  /**
   * Handle add row
   * Story 4.6 - Task 2.1: Wire row operations to API
   */
  const handleAddRow = useCallback(async () => {
    const initialValues: Record<string, CellValue> = {};
    localColumns.forEach((col) => {
      if (col.type === "multi-select")
        initialValues[col.id] = [] as unknown as CellValue;
      if (col.type === "checkbox") initialValues[col.id] = false;
      if (col.type === "rating") initialValues[col.id] = "Gray";
    });

    // Call API to create row
    try {
      setSaveStatus("saving");
      await apiAddRow(initialValues);
      setSaveStatus("saved");
    } catch {
      setSaveStatus("error");
    }
  }, [localColumns, apiAddRow]);

  const handleAttachFiles = (rowId: string, files: FileList) => {
    const newAttachments: Attachment[] = Array.from(files).map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file),
      uploadedBy: "Me",
      createdAt: Date.now(),
    }));
    const newRows = localRows.map((r) => {
      if (r.id === rowId) {
        return {
          ...r,
          attachments: [...(r.attachments || []), ...newAttachments],
          attachmentCount: (r.attachmentCount || 0) + newAttachments.length,
        };
      }
      return r;
    });
    updateRowsInternal(newRows);
    setSelection({
      gridId: sheetId,
      mode: "row",
      primaryRowId: rowId,
      selectedRowIds: [rowId],
      selectedColumnIds: [],
    });
    setSidebarTab("attachments");
  };

  const handleDeleteAttachment = async (args: {
    attachmentId: string;
    rowId: string;
  }) => {
    try {
      await deleteAttachment(args.rowId, args.attachmentId);

      const newRows = localRows.map((r) => {
        if (r.id === args.rowId) {
          const newAtts = (r.attachments || []).filter(
            (a) => a.id !== args.attachmentId,
          );
          return {
            ...r,
            attachments: newAtts,
            attachmentCount: newAtts.length,
          };
        }
        return r;
      });
      updateRowsInternal(newRows);

      // Invalidate the rows query to ensure fresh data from server
      queryClient.invalidateQueries({ queryKey: ["rows", sheetId] });
    } catch (error) {
      console.error("Failed to delete attachment:", error);
      // Could show a toast notification here
    }
  };

  const handleUploadAttachments = async (args: {
    rowId: string;
    files: FileList;
  }): Promise<Attachment[]> => {
    const uploadedAttachments: Attachment[] = [];

    for (let i = 0; i < args.files.length; i++) {
      const file = args.files[i];
      try {
        const attachment = await uploadAttachment(args.rowId, file);
        uploadedAttachments.push(attachment);
      } catch (error) {
        console.error("Failed to upload attachment:", file.name, error);
        // Continue with other files even if one fails
      }
    }

    // Update the row with new attachments
    if (uploadedAttachments.length > 0) {
      const newRows = localRows.map((r) => {
        if (r.id === args.rowId) {
          const currentAttachments = r.attachments || [];
          return {
            ...r,
            attachments: [...currentAttachments, ...uploadedAttachments],
            attachmentCount:
              currentAttachments.length + uploadedAttachments.length,
          };
        }
        return r;
      });
      updateRowsInternal(newRows);

      // Invalidate the rows query to ensure fresh data from server
      queryClient.invalidateQueries({ queryKey: ["rows", sheetId] });
    }

    return uploadedAttachments;
  };

  const handleOpenAttachments = (rowId: string) => {
    setSelection({
      gridId: sheetId,
      mode: "row",
      primaryRowId: rowId,
      selectedRowIds: [rowId],
      selectedColumnIds: [],
    });
    setSidebarTab("attachments");
  };

  const handleOpenComments = (rowId: string) => {
    setSelection({
      gridId: sheetId,
      mode: "row",
      primaryRowId: rowId,
      selectedRowIds: [rowId],
      selectedColumnIds: [],
    });
    setSidebarTab("comments");
  };

  const handleAddComment = (text: string, parentCommentId?: string) => {
    if (!activeRow) return;
    const newComment: Comment = {
      id: Math.random().toString(36).substr(2, 9),
      author: "Me",
      body: text,
      text: text,
      createdAt: Date.now(),
      parentCommentId,
    };
    const newRows = localRows.map((r) => {
      if (r.id === activeRow.id) {
        return {
          ...r,
          comments: [...(r.comments || []), newComment],
          commentCount: (r.commentCount || 0) + 1,
        };
      }
      return r;
    });
    updateRowsInternal(newRows);
  };

  const handleDeleteComment = (args: { commentId: string }) => {
    if (selection.primaryRowId) {
      const rowId = selection.primaryRowId;
      const newRows = localRows.map((r) => {
        if (r.id === rowId) {
          const newComments = (r.comments || []).filter(
            (c) =>
              c.id !== args.commentId && c.parentCommentId !== args.commentId,
          );
          return {
            ...r,
            comments: newComments,
            commentCount: newComments.length,
          };
        }
        return r;
      });
      updateRowsInternal(newRows);
    }
  };

  const handleEditComment = (args: { commentId: string; body: string }) => {
    if (selection.primaryRowId) {
      const rowId = selection.primaryRowId;
      const newRows = localRows.map((r) => {
        if (r.id === rowId) {
          const newComments = (r.comments || []).map((c) =>
            c.id === args.commentId
              ? {
                  ...c,
                  body: args.body,
                  text: args.body,
                  updatedAt: Date.now(),
                }
              : c,
          );
          return { ...r, comments: newComments };
        }
        return r;
      });
      updateRowsInternal(newRows);
    }
  };

  /**
   * Handle AI assistant actions
   * Story 4.6 - Task 2.1: Wire to API
   */
  const handleAiAction = useCallback(
    async (action: {
      type: string;
      rows?: { values: Record<string, CellValue> }[];
    }) => {
      if (action.type === "ADD_ROWS" && Array.isArray(action.rows)) {
        for (const r of action.rows) {
          try {
            setSaveStatus("saving");
            await apiAddRow(r.values || {});
            setSaveStatus("saved");
          } catch {
            setSaveStatus("error");
          }
        }
      }
    },
    [apiAddRow],
  );

  // Tab Handlers
  const handleTabClick = (tabId: string) => {
    setTabs(tabs.map((t) => ({ ...t, isActive: t.id === tabId })));
  };

  const handleTabRename = (tabId: string, newName: string) => {
    setTabs(tabs.map((t) => (t.id === tabId ? { ...t, name: newName } : t)));
  };

  const handleTabClose = (tabId: string) => {
    if (tabs.length > 1) {
      const closingActiveTab = tabs.find((t) => t.id === tabId)?.isActive;
      const newTabs = tabs.filter((t) => t.id !== tabId);

      if (closingActiveTab && newTabs.length > 0) {
        newTabs[0].isActive = true;
      }

      setTabs(newTabs);
    }
  };

  const handleTabReorder = (fromIndex: number, toIndex: number) => {
    const newTabs = [...tabs];
    const [movedTab] = newTabs.splice(fromIndex, 1);
    newTabs.splice(toIndex, 0, movedTab);
    setTabs(newTabs);
  };

  const handleAddTab = () => {
    const newTabNumber = tabs.length + 1;
    const newTab: Tab = {
      id: `sheet${newTabNumber}`,
      name: `Sheet ${newTabNumber}`,
      isActive: false,
    };
    setTabs([...tabs, newTab]);
  };

  const activeStyle =
    activeRow?.cellStyles?.[activeCol?.id || ""] || activeCol?.style || {};
  const configuringRow = configuringRowId
    ? localRows.find((r) => r.id === configuringRowId)
    : null;

  // --- Formula Bar Control Logic (Story 6.5: AC-6.5.1, AC-6.5.5) ---

  /**
   * Handle formula edit start - shows formula bar when user types `=` in a cell
   * AC-6.5.1: Formula bar becomes visible above grid when `=` is typed
   */
  const handleFormulaEditStart = useCallback(
    (rowId: string, colId: string, initialValue: string) => {
      setFormulaEditingCell({ rowId, colId });
      setShowFormulaBar(true);
      setCellEditValue(initialValue); // Initialize with the `=` character
      // Update selection to the cell being edited
      setSelection({
        gridId: sheetId,
        mode: "cell",
        primaryRowId: rowId,
        primaryColumnId: colId,
        selectedRowIds: [rowId],
        selectedColumnIds: [colId],
      });
    },
    [sheetId],
  );

  /**
   * Handle cell edit value change - syncs cell editor with formula bar
   * AC-6.5.2: Formula bar input and cell edit input stay synchronized
   */
  const handleCellEditChange = useCallback(
    (value: string) => {
      setCellEditValue(value);
      // Enable range capture if editing a formula with open parenthesis
      if (
        value.startsWith("=") &&
        value.endsWith("(") &&
        !rangeCaptureCallback
      ) {
        setRangeCaptureCallback(() => (expr: string) => {
          setCellEditValue(value + expr + ")");
          setRangeCaptureCallback(null);
        });
      } else if (!value.startsWith("=") || !value.endsWith("(")) {
        setRangeCaptureCallback(null);
      }
    },
    [rangeCaptureCallback],
  );

  /**
   * Handle formula bar value change - syncs formula bar with cell editor
   * AC-6.5.2: Formula bar input and cell edit input stay synchronized
   */
  const handleFormulaBarChange = useCallback(
    (value: string) => {
      // If typing a formula and not currently editing, start editing the selected cell
      if (
        value.startsWith("=") &&
        !showFormulaBar &&
        selection.type === "cell" &&
        selection.startRowId &&
        selection.startColId
      ) {
        handleFormulaEditStart(
          selection.startRowId,
          selection.startColId,
          value,
        );
      } else {
        setCellEditValue(value);
      }
    },
    [showFormulaBar, selection, handleFormulaEditStart],
  );

  /**
   * Handle formula bar cancel (Escape)
   * AC-6.5.5: Pressing Escape cancels without saving and hides formula bar
   */
  const handleFormulaCancel = useCallback(() => {
    setShowFormulaBar(false);
    setFormulaEditingCell(null);
    setCellEditValue("");
  }, []);

  // --- Range Selection Capture Logic ---

  const handleRangeCaptureRequest = useCallback(
    (callback: (expr: string) => void) => {
      setRangeCaptureCallback(() => callback);
    },
    [],
  );

  const handleRangeCaptureCancel = useCallback(() => {
    setRangeCaptureCallback(null);
  }, []);

  const handleRangeCaptured = useCallback(
    (expr: string) => {
      if (rangeCaptureCallback) {
        rangeCaptureCallback(expr);
        setRangeCaptureCallback(null);
      }
    },
    [rangeCaptureCallback],
  );

  // ---------------------------------------------------------------------------
  // LOADING / ERROR STATES (AC-4.6.1)
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="worksheet flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading sheet data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="worksheet flex items-center justify-center h-full">
        <div className="text-destructive">
          Error loading sheet: {error.message}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // EMPTY STATE
  // ---------------------------------------------------------------------------

  let emptyStateNode: React.ReactNode = null;
  if (localRows.length === 0) {
    emptyStateNode = (
      <>
        <div className="empty-state-title text-lg font-semibold">
          No rows yet
        </div>
        <p className="empty-state-text text-sm mt-1 mb-4">
          Click 'Row' in the toolbar to get started.
        </p>
        <button
          onClick={handleAddRow}
          className="empty-state-btn px-4 py-2 rounded text-sm font-medium transition-colors flex items-center gap-2 shadow-sm"
        >
          <IconPlus className="w-4 h-4" />
          Add Row
        </button>
      </>
    );
  } else if (filteredRows.length === 0) {
    emptyStateNode = (
      <>
        <div className="empty-state-icon p-3 rounded-full mb-3">
          <IconFilter className="w-6 h-6" />
        </div>
        <div className="empty-state-title text-lg font-semibold">
          No rows match your filters
        </div>
        <p className="empty-state-text text-sm mt-1">
          {searchQuery
            ? `No results for "${searchQuery}"`
            : "Try clearing filters or adjusting your search criteria."}
        </p>
        <button
          onClick={() => {
            setActiveFilterGroup(undefined);
            setSearchQuery("");
          }}
          className="empty-state-link mt-4 text-sm font-medium hover:underline"
        >
          Clear all filters
        </button>
      </>
    );
  }

  return (
    <div className="worksheet">
      {isFilterModalOpen && (
        <FilterModal
          columns={localColumns}
          initialGroup={activeFilterGroup}
          dateRangeFilter={dateRangeFilter}
          onApply={setActiveFilterGroup}
          onDateRangeFilterChange={setDateRangeFilter}
          onToggleColumnVisibility={toggleColumnVisibility}
          onClose={() => setIsFilterModalOpen(false)}
        />
      )}
      {editingColumn && (
        <ColumnConfigModal
          column={editingColumn}
          isNew={pendingInsertIndex !== null}
          columns={localColumns}
          onSave={handleColumnSave}
          onClose={() => {
            setEditingColumn(null);
            setPendingInsertIndex(null);
          }}
        />
      )}
      {configuringRow && (
        <RowConfigModal
          row={configuringRow}
          columns={localColumns}
          onSave={handleSaveRowConfig}
          onClose={() => setConfiguringRowId(null)}
        />
      )}
      {!hideToolbar && (
        <div className="worksheet-toolbar">
          <div className="flex items-center gap-4 mr-2">
            <div className="worksheet-title font-bold text-lg tracking-tight">
              ProGrid
            </div>
            <div className="flex flex-col items-start justify-center">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSave}
                  className={`toolbar-save-btn px-3 py-1 rounded text-sm font-medium whitespace-nowrap ${validationErrors.count > 0 ? "disabled" : ""}`}
                >
                  Save
                </button>
                {/* Save status indicator (AC-4.6.3) */}
                <span
                  className={`text-xs font-medium ${saveStatus === "saving" ? "text-amber-500" : saveStatus === "error" ? "text-red-500" : "text-green-500/70"}`}
                >
                  {saveStatus === "saving"
                    ? "Saving..."
                    : saveStatus === "error"
                      ? "Error"
                      : "Saved"}
                </span>
                {validationErrors.count > 0 && (
                  <button
                    onClick={handleValidationClick}
                    className="validation-error-link text-xs font-medium px-2 py-0.5 rounded border transition-colors cursor-pointer"
                    title="Click to find first error"
                  >
                    {validationErrors.count} validation{" "}
                    {validationErrors.count === 1 ? "error" : "errors"}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="toolbar-divider"></div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleUndo}
              disabled={!historyState.canUndo}
              className="toolbar-btn"
              title="Undo (Ctrl+Z)"
            >
              <IconUndo className="w-4 h-4" />
            </button>
            <button
              onClick={handleRedo}
              disabled={!historyState.canRedo}
              className="toolbar-btn"
              title="Redo (Ctrl+Shift+Z)"
            >
              <IconRedo className="w-4 h-4" />
            </button>

            {/* Merged Add Dropdown */}
            <div className="relative" ref={addMenuRef}>
              <button
                onClick={() => setShowAddMenu(!showAddMenu)}
                className="toolbar-btn-text"
                title="Add Row or Column"
              >
                <IconPlus className="w-4 h-4 toolbar-icon-accent" />
                <span className="text-xs font-medium">Add</span>
              </button>
              {showAddMenu && (
                <div className="toolbar-dropdown absolute top-full left-0 mt-1 w-40 rounded-lg shadow-xl border z-[60] overflow-hidden py-1">
                  <div
                    className="toolbar-dropdown-item px-4 py-2 cursor-pointer flex items-center gap-2 text-sm"
                    onClick={() => {
                      handleAddRow();
                      setShowAddMenu(false);
                    }}
                  >
                    <IconTable className="w-4 h-4" />
                    Row
                  </div>
                  <div
                    className="toolbar-dropdown-item px-4 py-2 cursor-pointer flex items-center gap-2 text-sm"
                    onClick={() => {
                      handleInitiateAddColumn();
                      setShowAddMenu(false);
                    }}
                  >
                    <IconColumns className="w-4 h-4" />
                    Column
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="toolbar-divider"></div>
          <div className="flex items-center gap-1">
            <button
              className={`toolbar-btn ${activeStyle.bold ? "active" : ""}`}
              onClick={() => handleApplyCellStyle({ bold: !activeStyle.bold })}
              title="Bold"
            >
              <IconBold className="w-4 h-4" />
            </button>
            <button
              className={`toolbar-btn ${activeStyle.italic ? "active" : ""}`}
              onClick={() =>
                handleApplyCellStyle({ italic: !activeStyle.italic })
              }
              title="Italic"
            >
              <IconItalic className="w-4 h-4" />
            </button>
            <button
              className={`toolbar-btn ${activeStyle.underline ? "active" : ""}`}
              onClick={() =>
                handleApplyCellStyle({ underline: !activeStyle.underline })
              }
              title="Underline"
            >
              <IconUnderline className="w-4 h-4" />
            </button>
          </div>
          <div className="toolbar-divider"></div>
          <div className="flex items-center gap-1">
            <button
              className={`toolbar-btn ${activeStyle.align === "left" ? "active" : ""}`}
              onClick={() => handleApplyCellStyle({ align: "left" })}
              title="Align Left"
            >
              <IconAlignLeft className="w-4 h-4" />
            </button>
            <button
              className={`toolbar-btn ${activeStyle.align === "center" ? "active" : ""}`}
              onClick={() => handleApplyCellStyle({ align: "center" })}
              title="Align Center"
            >
              <IconAlignCenter className="w-4 h-4" />
            </button>
            <button
              className={`toolbar-btn ${activeStyle.align === "right" ? "active" : ""}`}
              onClick={() => handleApplyCellStyle({ align: "right" })}
              title="Align Right"
            >
              <IconAlignRight className="w-4 h-4" />
            </button>
            <div className="w-2"></div>
            <button
              className="toolbar-btn"
              onClick={() => handleIndentRows("outdent")}
              title="Outdent"
            >
              <IconOutdent className="w-4 h-4" />
            </button>
            <button
              className="toolbar-btn"
              onClick={() => handleIndentRows("indent")}
              title="Indent"
            >
              <IconIndent className="w-4 h-4" />
            </button>
          </div>
          <div className="toolbar-divider"></div>
          <div className="flex items-center gap-2">
            <select
              className="toolbar-select w-20"
              value={activeStyle.fontSize || "md"}
              onChange={(e) =>
                handleApplyCellStyle({ fontSize: e.target.value as any })
              }
            >
              <option value="sm">Small</option>
              <option value="md">Normal</option>
              <option value="lg">Large</option>
            </select>
          </div>
          <div className="toolbar-divider"></div>
          <div className="flex items-center gap-2 flex-1">
            <button
              className={`toolbar-btn ${activeFilterGroup || dateRangeFilter || localColumns.some((c) => c.hidden) ? "filter-active" : ""}`}
              onClick={() => setIsFilterModalOpen(true)}
              title="Filter Rows & Columns"
            >
              <IconFilter className="w-4 h-4" />
            </button>

            <div
              className={`toolbar-search-container flex items-center transition-all duration-300 overflow-hidden ${isSearchOpen ? "w-48 open" : "w-8"}`}
            >
              {isSearchOpen ? (
                <>
                  <div className="pl-2 toolbar-search-icon">
                    <IconSearch className="w-3.5 h-3.5" />
                  </div>
                  <input
                    type="text"
                    className="toolbar-search-input bg-transparent border-none outline-none text-xs w-full px-2 py-1 h-8"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setIsSearchOpen(false);
                    }}
                    className="toolbar-search-close pr-2"
                  >
                    <IconX className="w-3 h-3" />
                  </button>
                </>
              ) : (
                <button
                  className="toolbar-btn"
                  onClick={() => setIsSearchOpen(true)}
                  title="Search"
                >
                  <IconSearch className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 pl-4 toolbar-section-border">
            {/* TODO: Re-enable formula bar toggle when formula engine is fully implemented (Epic 6+) */}
            {/* <button onClick={() => setShowFormulaBar(!showFormulaBar)} className={`toolbar-icon-btn p-1.5 rounded-md ${showFormulaBar ? 'active' : ''}`} title="Toggle Formula Bar">{showFormulaBar ? <IconEye className="w-5 h-5" /> : <IconEyeOff className="w-5 h-5" />}</button> */}
            <button
              onClick={() => setSidebarTab((p) => (p === "ai" ? "none" : "ai"))}
              className={`toolbar-ai-btn p-1.5 rounded-md ${sidebarTab === "ai" ? "active" : ""}`}
              title="AI Assistant"
            >
              <IconSparkles className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
      {/* Filter chips row removed per user request - filters managed via modal */}
      {(showFormulaBar || selection.type === "cell") && (
        <div className="worksheet-formula-bar">
          <FormulaBar
            activeCellAddress={activeAddress}
            currentValue={activeValue}
            currentFormula={activeFormula}
            onCommit={handleFormulaCommit}
            onCancel={handleFormulaCancel}
            onRangeCaptureRequest={handleRangeCaptureRequest}
            onRangeCaptureCancel={handleRangeCaptureCancel}
            columns={localColumns.map((c) => ({ id: c.id, label: c.label }))}
            externalValue={cellEditValue}
            onChange={handleFormulaBarChange}
            isCapturing={!!rangeCaptureCallback}
          />
        </div>
      )}
      <div className="worksheet-workspace">
        <div className="worksheet-grid-area">
          <DataGrid
            gridId={sheetId}
            rows={filteredRows}
            columns={localColumns}
            selection={selection}
            sortConfig={sortConfig}
            onSort={handleSort}
            onChange={handleGridChange}
            onSelectionChange={setSelection}
            onAttachFiles={handleAttachFiles}
            onOpenAttachments={handleOpenAttachments}
            onOpenComments={handleOpenComments}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onEditColumn={handleEditColumn}
            onInsertColumn={handleInitiateInsertColumn}
            onDeleteColumn={handleDeleteColumn}
            onToggleColumnVisibility={toggleColumnVisibility}
            onColumnReorder={handleColumnReorder}
            onRowConfig={handleRowConfig}
            onRowReorder={handleRowReorder}
            validationErrors={validationErrors.errors}
            emptyState={emptyStateNode}
            isFormulaRangeCaptureActive={!!rangeCaptureCallback}
            onRangeCaptured={handleRangeCaptured}
            onFormulaEditStart={handleFormulaEditStart}
            onCellEditChange={handleCellEditChange}
            externalEditValue={showFormulaBar ? cellEditValue : undefined}
          />
        </div>
        <RightSidebar
          activeTab={sidebarTab}
          onClose={() => setSidebarTab("none")}
          onTabChange={(t) => setSidebarTab(t)}
          selection={selection}
          allRows={localRows}
          allColumns={localColumns}
          currentAttachments={activeRow?.attachments || []}
          currentComments={activeRow?.comments || []}
          onAddComment={handleAddComment}
          onUploadAttachments={handleUploadAttachments}
          onDeleteAttachment={handleDeleteAttachment}
          onDeleteComment={handleDeleteComment}
          onEditComment={handleEditComment}
          onAiAction={handleAiAction}
        />
      </div>

      <GridStatusBar
        rows={filteredRows}
        columns={localColumns}
        selection={selection}
      />

      {!hideTabBar && (
        <TabBar
          tabs={tabs}
          onTabClick={handleTabClick}
          onTabRename={handleTabRename}
          onTabClose={handleTabClose}
          onTabReorder={handleTabReorder}
          onAddTab={handleAddTab}
        />
      )}
    </div>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

/**
 * Backward-compatible alias for SheetView
 * @deprecated Use SheetView instead - WorksheetView will be removed in future versions
 */
export const WorksheetView = SheetView;

export default SheetView;
